import React, { useState } from 'react';
import { useAppContext } from '../App';
import { sanitizeInput, isValidEmail } from '../utils/security';

const ContactForm = () => {
  const {
    isContactFormOpen,
    setIsContactFormOpen,
    contactSupplier,
    showNotification
  } = useAppContext();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isContactFormOpen || !contactSupplier) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Sanitize all inputs
    const sanitizedData = {
      name: sanitizeInput(formData.name),
      email: sanitizeInput(formData.email),
      company: sanitizeInput(formData.company),
      message: sanitizeInput(formData.message)
    };

    // Validate form data
    if (!sanitizedData.name.trim()) {
      showNotification('Please enter your name', 'error');
      setIsSubmitting(false);
      return;
    }

    if (!sanitizedData.email.trim() || !isValidEmail(sanitizedData.email)) {
      showNotification('Please enter a valid email address', 'error');
      setIsSubmitting(false);
      return;
    }

    if (!sanitizedData.message.trim()) {
      showNotification('Please enter a message', 'error');
      setIsSubmitting(false);
      return;
    }

    if (sanitizedData.message.length > 2000) {
      showNotification('Message must be less than 2000 characters', 'error');
      setIsSubmitting(false);
      return;
    }

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 500));

    showNotification(`Message sent to ${contactSupplier}!`, 'success');
    setIsContactFormOpen(false);
    setFormData({ name: '', email: '', company: '', message: '' });
    setIsSubmitting(false);
  };

  const handleClose = () => {
    setIsContactFormOpen(false);
    setFormData({ name: '', email: '', company: '', message: '' });
  };

  return (
    <>
      <div className="contact-overlay" onClick={handleClose} />
      <div className="contact-form-container">
        <div className="contact-header">
          <div className="contact-logo">
            <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="contactGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#3b82f6" />
                  <stop offset="100%" stopColor="#06b6d4" />
                </linearGradient>
              </defs>
              <path d="M14 8 L14 16 L8 32 C7 34 8 36 10 36 L30 36 C32 36 33 34 32 32 L26 16 L26 8" stroke="url(#contactGradient)" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
              <rect x="13" y="4" width="14" height="4" rx="1" fill="url(#contactGradient)"/>
            </svg>
          </div>
          <h2 className="contact-title">Contact {contactSupplier}</h2>
          <button className="modal-close" onClick={handleClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <form className="contact-form-body" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="contact-name">Your Name *</label>
            <input
              id="contact-name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your name"
              disabled={isSubmitting}
              maxLength={100}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="contact-email">Email *</label>
            <input
              id="contact-email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              disabled={isSubmitting}
              maxLength={254}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="contact-company">Company (Optional)</label>
            <input
              id="contact-company"
              name="company"
              type="text"
              value={formData.company}
              onChange={handleChange}
              placeholder="Enter your company name"
              disabled={isSubmitting}
              maxLength={100}
            />
          </div>

          <div className="form-group">
            <label htmlFor="contact-message">Message *</label>
            <textarea
              id="contact-message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Enter your inquiry or message..."
              disabled={isSubmitting}
              rows={4}
              maxLength={2000}
              required
            />
            <span className="char-count">{formData.message.length}/2000</span>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </div>
        </form>

        <style jsx>{`
          .contact-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.6);
            backdrop-filter: blur(4px);
            z-index: 200;
            animation: fadeIn 0.2s ease;
          }

          .contact-form-container {
            position: fixed;
            bottom: var(--space-xl);
            right: var(--space-xl);
            width: 100%;
            max-width: 500px;
            background: linear-gradient(135deg, rgba(30, 41, 59, 0.98), rgba(15, 23, 42, 0.98));
            border: 1px solid rgba(59, 130, 246, 0.3);
            border-radius: var(--radius-xl);
            z-index: 201;
            animation: slideUp 0.3s ease;
            overflow: hidden;
          }

          .contact-header {
            display: flex;
            align-items: center;
            gap: var(--space-md);
            padding: var(--space-lg) var(--space-xl);
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          }

          .contact-logo {
            width: 40px;
            height: 40px;
          }

          .contact-logo svg {
            width: 100%;
            height: 100%;
          }

          .contact-title {
            flex: 1;
            font-family: var(--font-display);
            font-size: var(--font-xl);
            font-weight: 600;
            color: var(--color-neutral-50);
            margin: 0;
          }

          .modal-close {
            width: 36px;
            height: 36px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(255, 255, 255, 0.1);
            border: none;
            border-radius: 50%;
            color: var(--color-neutral-300);
            cursor: pointer;
            transition: all var(--transition-fast);
          }

          .modal-close:hover {
            background: rgba(239, 68, 68, 0.3);
            color: #ef4444;
          }

          .contact-form-body {
            padding: var(--space-xl);
            display: flex;
            flex-direction: column;
            gap: var(--space-md);
          }

          .form-group {
            display: flex;
            flex-direction: column;
            gap: var(--space-xs);
            position: relative;
          }

          .form-group label {
            font-size: var(--font-sm);
            font-weight: 500;
            color: var(--color-neutral-300);
          }

          .form-group input,
          .form-group textarea {
            padding: var(--space-sm) var(--space-md);
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: var(--radius-md);
            color: var(--color-neutral-100);
            font-family: var(--font-body);
            font-size: var(--font-base);
            transition: all var(--transition-fast);
            resize: vertical;
          }

          .form-group input:focus,
          .form-group textarea:focus {
            outline: none;
            border-color: var(--color-accent-400);
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
          }

          .form-group input::placeholder,
          .form-group textarea::placeholder {
            color: var(--color-neutral-500);
          }

          .char-count {
            font-size: var(--font-xs);
            color: var(--color-neutral-500);
            text-align: right;
          }

          .form-actions {
            display: flex;
            gap: var(--space-sm);
            margin-top: var(--space-md);
          }

          .form-actions .btn {
            flex: 1;
          }

          @keyframes slideUp {
            from {
              transform: translateY(20px);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
        `}</style>
      </div>
    </>
  );
};

export default ContactForm;
